var Repository                = require("app/Domain/Ott/Api/V2/Repository"),
    circularSafeJSONStringify = require("app/utils/circularSafeJSONStringify"),
    config                    = require("app/config"),
    get                       = require("object-path").get,
    kind                      = require("enyo/kind"),
    logger                    = require("enyo/logger"),
    quickMacroize             = require("enyo/macroize").quickMacroize;

/**
 * This object retrieves Dashboard items from the API.
 */
module.exports = kind.singleton(/** @lends app.Domain.Ott.Api.V2.DataAccess.Dashboard.Repository.prototype */ {

    /**
     * @private
     */
    name: "app.Domain.Ott.Api.V2.DataAccess.Dashboard.Repository",

    /**
     * @private
     */
    kind: Repository,

    // API
    // --------------------------------

    /**
     * Load root category as dashboard. The dashboard will contain a list of
     * truncated sub-categories.
     *
     * @param    {boolean}  skipCache  Whether to skip the cache
     * @param    {number}   language   The language
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    getRoot: function(skipCache, language) {
        var key      = ["dashboard", "root", language].join("/"),
            lifetime = get(config, "cache.domain/ott/api/v2/dashboard/root", 0),
            task     = this.tasks[key],
            endpoint;

        if (this.cache.isset(key) && !skipCache) {
            return Promise.resolve(this.cache.get(key));
        }

        // Start loading
        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/dashboard/root?language={$language}&mode=expanded", {
                ottApi:   config.ottApi.url,
                language: encodeURIComponent(language)
            }));

            task = this.tasks[key] = this._get(endpoint.toString());

            task.promise
                .then(this.bindSafely(function(response) {
                    if (response.status === "ok") {
                        this.cache.set(key, response, lifetime);
                    }

                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        return task.promise;
    },

    /**
     * Load a list of titles for the top-level categories.
     *
     * @param    {boolean}  skipCache  Whether to skip the cache
     * @param    {number}   language   The language
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    getRootTitles: function(skipCache, language) {
        var key      = ["dashboard", "root", "titles", language].join("/"),
            lifetime = get(config, "cache.domain/ott/api/v2/dashboard/root/titles", 0),
            task     = this.tasks[key],
            endpoint;

        if (this.cache.isset(key) && !skipCache) {
            return Promise.resolve(this.cache.get(key));
        }

        // Start loading
        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/dashboard/root/titles?language={$language}&mode=expanded", {
                ottApi:   config.ottApi.url,
                language: encodeURIComponent(language)
            }));

            task = this.tasks[key] = this._get(endpoint);

            task.promise
                .then(this.bindSafely(function(response) {
                    if (response.status === "ok") {
                        this.cache.set(key, response, lifetime);
                    }

                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        return task.promise;
    },

    /**
     * Load category as dashboard. The dashboard will contain a list of truncated
     * sub-categories.
     *
     * @param    {boolean}  skipCache  Whether to skip the cache
     * @param    {number}   language   The language
     * @param    {string}   slug       The slug
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    getCategoryBySlug: function(skipCache, language, slug) {
        var key      = ["dashboard", "category", slug, language].join("/"),
            lifetime = get(config, "cache.domain/ott/api/v2/dashboard/category/{slug}", 0),
            task     = this.tasks[key],
            endpoint;

        if (this.cache.isset(key) && !skipCache) {
            return Promise.resolve(this.cache.get(key));
        }

        // Start loading
        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/dashboard/slug/{$slug}?language={$language}&mode=expanded", {
                ottApi:   config.ottApi.url,
                slug:     slug,
                language: encodeURIComponent(language)
            }));

            task = this.tasks[key] = this._get(endpoint);

            task.promise
                .then(this.bindSafely(function(response) {
                    if (response.status === "ok") {
                        this.cache.set(key, response, lifetime);
                    }

                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        return task.promise;
    },

    /**
     * Load category as dashboard. The dashboard will contain a list of
     * truncated sub-categories.
     *
     * @param    {boolean}  skipCache  Whether to skip the cache
     * @param    {number}   language   The language
     * @param    {string}   id         The id
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    getCategoryById: function(skipCache, language, id) {
        var key      = ["dashboard", "category", id, language].join("/"),
            lifetime = get(config, "cache.domain/ott/api/v2/dashboard/category/{id}", 0),
            task     = this.tasks[key],
            endpoint;

        if (this.cache.isset(key) && !skipCache) {
            return Promise.resolve(this.cache.get(key));
        }

        // Start loading
        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/dashboard/{$id}?language={$language}&mode=expanded", {
                ottApi:   config.ottApi.url,
                id:       id,
                language: encodeURIComponent(language)
            }));

            task = this.tasks[key] = this._get(endpoint);

            task.promise
                .then(this.bindSafely(function(response) {
                    if (response.status === "ok") {
                        this.cache.set(key, response, lifetime);
                    }

                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));
        }

        return task.promise;
    },

    /**
     * Checks if a category as a dashboard.
     *
     * @param    {boolean}  skipCache  Whether to skip the cache
     * @param    {number}   language   The language
     * @param    {string}   slug       The slug
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    getCategoryHasDashboardBySlug: function(skipCache, language, slug) {
        var key      = ["dashboard", "category", slug, "has-dashboard", language].join("/"),
            lifetime = get(config, "cache.domain/ott/api/v2/dashboard/category/{slug}/has-dashboard", 0),
            task     = this.tasks[key],
            endpoint;

        if (this.cache.isset(key) && !skipCache) {
            return Promise.resolve(this.cache.get(key));
        }

        // Start loading
        if (!task) {
            endpoint = new URL(quickMacroize("{$ottApi}/dashboard/slug/{$slug}/has-dashboard?language={$language}&mode=expanded", {
                ottApi:   config.ottApi.url,
                slug:     slug,
                language: encodeURIComponent(language)
            }));

            task = this.tasks[key] = this._buildXhrTask();

            task.execute = function() {
                var xhr;

                task.xhr = xhr = new XMLHttpRequest();
                xhr.open("GET", endpoint);
                xhr.setRequestHeader("Content-Type", "text/plain");

                xhr.onreadystatechange = function() {
                    if (xhr.readyState === 4) {
                        if (xhr.status === 0 && task.isRenewing) {
                        // We are renewing the task, we can skip handling the
                        // canceled response and await the result of the renewed
                        // task.
                        }
                        else {
                            task.handleResult();
                        }
                    }
                };
                xhr.onreadystatechange.bind(this);
                xhr.send();
            };

            task.handleResult = function() {
                var response,
                    xhr     = task.xhr,
                    resolve = task.resolve;

                try {
                    response = xhr.responseText || null;
                }
                catch (error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error, {
                            extra: {
                                xhr: circularSafeJSONStringify(xhr)
                            }
                        });
                    }
                    response = null;
                }

                switch (xhr.status) {
                    case 0:
                        resolve({
                            status: "canceled"
                        });
                        break;
                    case 200:
                        resolve({
                            status: "ok",
                            data:   response
                        });
                        break;
                    case 404:
                        resolve({
                            status: "error",
                            type:   response || "not_found_error"
                        });
                        break;
                    default:
                        resolve({
                            status:     "error",
                            type:       response && response.type ? response.type : "unknown_error",
                            httpStatus: xhr.status
                        });
                        break;
                }
            };

            task.promise
                .then(this.bindSafely(function(response) {
                    if (response.status === "ok") {
                        this.cache.set(key, response, lifetime);
                    }

                    return response;
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    return Promise.resolve({
                        status: "rejected",
                        type:   "task_rejected",
                        error:  error
                    });
                }))
                .finally(this.bindSafely(function() {
                    delete this.tasks[key];
                }));

            // Run the task
            task.execute();
        }

        return task.promise;
    }

});
